import React, { Fragment, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import cx from "classnames";
import { cloneDeep } from "lodash";
import Loading from "../../../Global/Loading";
import useReportSelections from "../../util/useReportSelections";
import actions from "../../state/roster/actions";
import types from "../../state/roster/constants";
import "./index.scss";
import { useTranslation } from "react-i18next";

const numberOfRetries = 3;

export default function BatchPrint(props) {
  const [data, setData] = useState(null);
  const [printQueue, setPrintQueue] = useState(null);
  const [errorList, setErrorList] = useState(null);
  const [timeRemain, setTimeRemain] = useState(null)
  const students = useSelector((state) => state.reports.roster.data.student);
  const selections = useReportSelections();
  const { classroom } = selections;
  const dispatch = useDispatch();
  const {t} = useTranslation("reports");

  // exit batch print mode on unmount
  useEffect(() => {
    return onExitPrinting;
  }, []);

  // initialize the printQueue
  useEffect(() => {
    if (!students) return;
    setPrintQueue(cloneDeep(students.map((s) => {return {...s.ui, first_name: s.first_name}})));
  }, [students]);

  // print the contents of printQueue
  useEffect(() => {
    if (!printQueue) return;
    const params = { isMounted: true };
    getBatchData(params);
    return () => (params.isMounted = false);
  }, [printQueue]);

  // set document title based on classroom name for printing
  useEffect(() => {
    if (!classroom) return;
    document.title = `${props.title} ${classroom.label} ${moment().format(
      "MM-DD-YY"
    )}`;
    return () => (document.title = "");
  }, [classroom]);

  async function getBatchData(params) {
    setData(null);
    const studentQueryList = cloneDeep(printQueue);
    const batchData = [];
    let lapsed = 0
    let totalLapsed = 0
    let avgLapsed = 0
    let i=0;
    do {
      console.log("Batch Print: studentQueryList", studentQueryList);
      for (let j = 0; j<studentQueryList.length; j++) {
        if (!params.isMounted) return; // exit getBatchData if not mounted
        setTimeRemain(parseInt((avgLapsed / 1000).toFixed(0)) * studentQueryList.length - j)
        const s = studentQueryList[j];
        try {
          if (!s) continue;
          studentQueryList[j] = null // remove student from the queryList
          let startTime = performance.now()
          batchData[j] = await props.getStudentData(s, selections);
          studentQueryList[j] = null;
          await new Promise((resolve) => setTimeout(resolve, 1000)); // wait 1 sec before next iteration
          lapsed = performance.now() - startTime
          totalLapsed = totalLapsed + lapsed
          avgLapsed = totalLapsed / (j || 1)
        } catch (e) {
          console.log(`Batch Print: getStudentData error for ${s.label}`, e);
          // add the student back to queryList if errorType was LimitExceededException
          if (e.errors && e.errors[0].errorType === 'LimitExceededException')
            studentQueryList[j] = s;
        }
      }
      i++;
    } while (i < numberOfRetries && studentQueryList.filter((x) => x).length > 0)

    console.log("Batch Print: data", batchData);
    console.log("Batch Print errorList", errorList);
    if (!params.isMounted) return; // cancel render & print if not mounted
    setErrorList(cloneDeep(studentQueryList.filter((x) => x)));
    setData(batchData);
    if (batchData[0]) setTimeout(window.print, 1000);
  }

  if (!data)
    return (
      <div className="batch-print-loading">
        <div className="batch-print-text">{t("generating_batch_report")}</div>
        {renderTimeRemains(timeRemain)}
        <div className="batch-print-spinner">
          <Loading />
        </div>
      </div>
    );

  const error = errorList && errorList[0];

  return (
    <Fragment>
      <div className="batch-print-window">
        {error && renderRetryMessage()}
        {error && renderRetryButton()}
        {renderExitButton()}
      </div>
      {data.map(props.renderReport)}
    </Fragment>
  );

  function renderRetryMessage() {
    const multi = errorList.length > 1;
    return (
      <div className="batch-print-text" style={{ marginBottom: 40 }}>
        Select Continue to print report{multi && "s"} for {errorList.length}{" "}
        more student{multi && "s"}:
        <div className="batch-print-student-box">
          {errorList.map((s, i) => <div key={i}>{s.label}</div>)}
        </div>
      </div>
    );
  }

  function renderRetryButton() {
    return (
      <div
        className="batch-print-button"
        onClick={() => setPrintQueue(cloneDeep(errorList))}
      >
        {t("Continue")}
      </div>
    );
  }

  function renderExitButton() {
    return (
      <div
        className={cx("batch-print-button", { error })}
        onClick={onExitPrinting}
      >
        {t("Exit Batch Print")}
      </div>
    );
  }

  function renderTimeRemains(seconds){
    if(!seconds) return null
    let str = t("seconds_left", {seconds});
    if (seconds / 60 > 1) str = t("minutes_left", {minutes: (seconds/60).toFixed(0)});
    return <p className="batch-print-time-remaining">{str}</p>
  }

  function onExitPrinting() {
    dispatch(actions[types.BATCH_PRINT](false));
  }
}
